/**
 * The live-update bus — "state emission" for the two cross-cutting feeds that
 * must stay current everywhere they appear: `activity` and `suggestions`.
 *
 * Three moving parts:
 *   • a process-local emitter (`subscribeLive`/`emitLive`) — the fan-out within
 *     one tab, so every mounted surface bound to a topic revalidates together;
 *   • a `BroadcastChannel` — fans the same signal across other tabs/windows of
 *     the app, instantly (no waiting for their own poll to come around);
 *   • `<LiveBus>` — one client poller of `/api/live` (cheap tenant-scoped
 *     change tokens). When a token changes it emits that topic locally AND
 *     broadcasts it. ONE poller drives the whole fleet — surfaces themselves do
 *     not each poll the feed.
 *
 * Consumers use `useLiveResource(op, args, { seed, topic })`: an SWR query
 * seeded with server `fallbackData` that re-fetches whenever its topic fires.
 * Explicit local mutations (a dismiss, a "clear all") call `notifyLive(topic)`
 * so other surfaces/tabs update immediately rather than on the next poll.
 *
 * No new transport (no websocket): this generalizes the repo's existing
 * poll-a-version-token-then-mutate idiom (the shell's VersionWatcher) into a
 * shared, topic-routed primitive.
 */

import { useEffect, useRef } from "react";
import type { SWRConfiguration, SWRResponse } from "swr";
import { apiCall, useApi } from "./client";
import { live, type LiveTopic } from "./contracts/live";
import type { Args, Operation } from "./types";

// ---- process-local emitter ----

const listeners = new Map<string, Set<() => void>>();

/** Subscribe to a topic; returns an unsubscribe fn. */
export function subscribeLive(topic: LiveTopic, cb: () => void): () => void {
  let set = listeners.get(topic);
  if (!set) {
    set = new Set();
    listeners.set(topic, set);
  }
  set.add(cb);
  return () => {
    set!.delete(cb);
  };
}

/** Fire every local subscriber of a topic (this tab only). */
export function emitLive(topic: LiveTopic): void {
  const set = listeners.get(topic);
  if (!set) return;
  for (const cb of set) {
    try {
      cb();
    } catch {
      /* a misbehaving subscriber must not break the fan-out */
    }
  }
}

// ---- cross-tab channel ----

const CHANNEL_NAME = "excellent:live";
let channel: BroadcastChannel | null | undefined;

function getChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
    channel = null;
    return channel;
  }
  channel = new BroadcastChannel(CHANNEL_NAME);
  // A message from another tab fans out LOCALLY only — never re-broadcast, or
  // two tabs would echo each other forever.
  channel.onmessage = (ev: MessageEvent) => {
    const topic = (ev.data as { topic?: LiveTopic } | null)?.topic;
    if (topic) emitLive(topic);
  };
  return channel;
}

/** Tell other tabs/windows a topic changed (does NOT fire local subscribers). */
export function broadcastLive(topic: LiveTopic): void {
  try {
    getChannel()?.postMessage({ topic });
  } catch {
    /* channel closed / unsupported — local emit still covers this tab */
  }
}

/**
 * Announce a topic change everywhere — this tab and every other. Call after an
 * explicit mutation (dismiss, clear, create) so the change is reflected without
 * waiting for the next `<LiveBus>` poll.
 */
export function notifyLive(topic: LiveTopic): void {
  emitLive(topic);
  broadcastLive(topic);
}

// ---- React hooks ----

/** Run `cb` whenever `topic` fires. `cb` may change between renders. */
export function useLiveTopic(topic: LiveTopic, cb: () => void): void {
  const ref = useRef(cb);
  ref.current = cb;
  useEffect(() => {
    // Touch the channel so cross-tab delivery is wired up wherever a live
    // consumer mounts, not only where <LiveBus> happens to render.
    getChannel();
    return subscribeLive(topic, () => ref.current());
  }, [topic]);
}

/**
 * An SWR query that stays live on the bus. Seed it with server-rendered data
 * (`fallbackData`) so the surface paints instantly, then it revalidates on
 * every `topic` signal. `refreshInterval` is an optional heartbeat safety net
 * for time-based changes that no row fingerprint would catch.
 */
export function useLiveResource<TParams, TQuery, TBody, TResult>(
  op: Operation<TParams, TQuery, TBody, TResult>,
  args: Args<TParams, TQuery, TBody> | null,
  opts: {
    topic: LiveTopic;
    seed?: TResult;
    refreshInterval?: number;
  } & SWRConfiguration<TResult>,
): SWRResponse<TResult> {
  const { topic, seed, refreshInterval, ...rest } = opts;
  const swr = useApi(op, args, {
    fallbackData: seed,
    refreshInterval,
    ...rest,
  });
  useLiveTopic(topic, () => {
    void swr.mutate();
  });
  return swr;
}

// ---- the poller (mount once in the shell) ----

/**
 * One client poller of `/api/live`. Diffs each topic's token against the last
 * poll; on change, emits locally + broadcasts. Returns `null` — it is wiring,
 * not UI. Mount a single instance high in the app shell.
 */
export function LiveBus({ intervalMs = 5_000 }: { intervalMs?: number }): null {
  const last = useRef<Record<string, string> | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;

    async function tick() {
      if (cancelled) return;
      // Visibility-aware (DEV-MEMORY-PRD M4): a hidden tab stops polling so
      // the dev server gets idle windows; the visibilitychange listener below
      // resumes with an immediate catch-up poll. Pause keys off `hidden` (tab
      // truly not rendered), never blur — a visible-but-unfocused tab on a
      // second monitor keeps its live updates.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      try {
        const { tokens } = await apiCall(live.tokens, {});
        const prev = last.current;
        last.current = tokens;
        // First successful poll only establishes the baseline — surfaces are
        // already seeded with matching server data, so don't revalidate yet.
        if (prev) {
          for (const topic of Object.keys(tokens) as LiveTopic[]) {
            if (prev[topic] !== tokens[topic]) {
              emitLive(topic);
              broadcastLive(topic);
            }
          }
        }
      } catch {
        /* transient (nav, server restart) — keep polling */
      }
      timer = window.setTimeout(tick, intervalMs);
    }

    function onVisibility() {
      if (document.visibilityState !== "visible") return;
      // Catch up at once (the token diff covers everything missed while
      // hidden), then the normal cadence resumes from this tick.
      if (timer !== null) window.clearTimeout(timer);
      void tick();
    }
    document.addEventListener("visibilitychange", onVisibility);

    void tick();
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [intervalMs]);

  return null;
}
